import { AxiosError } from 'axios'
import produce from 'immer'
import React from 'react'
import { QueryStatus, QueryObserverOptions, UseQueryResult } from 'react-query'
import create, { State } from 'zustand'

import { GameDay, PagedGameDayResponse } from 'types/api/gamedays/scenarios'

import { useGremlinPagination, useGremlinQuery } from './use-gremlin-query'

const GAMEDAYS_API_URL = '/gamedays'

export interface GameDaysState extends State {
  status: QueryStatus
  gamedays: {
    [guid: string]: GameDay
  }
  set: (fn: (state: GameDaysState) => void) => void
  clearAll: () => void
}

export const useGameDaysStore = create<GameDaysState>((set) => ({
  status: 'loading',
  gamedays: {},
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.gameDays = {}
      })
    )
}))

export function useGameDays(enabled = true): void {
  const set = useGameDaysStore((state) => state.set)
  const pagedAllFetchState = useGremlinPagination<PagedGameDayResponse>(
    // Use long form endpoint to ensure react-query key cache hit
    GAMEDAYS_API_URL,
    {},
    {
      onSuccess: (data) => {
        set((state) => {
          const all = data?.pages?.flatMap((page) => page.items)
          state.gamedays = {
            ...state.gamedays,
            ...Object.fromEntries(all.map((s) => [s.guid, s]))
          }
        })
      },
      onSettled: (data, error) => {
        set((state) => {
          state.status =
            error && (!data || typeof data === 'string') ? 'error' : 'success'
        })
      },
      refetchInterval: 5 * 60_000,
      staleTime: 5 * 60_000,
      enabled
    }
  )

  React.useEffect(() => {
    let timeout
    if (
      pagedAllFetchState.hasNextPage &&
      !pagedAllFetchState.isFetchingNextPage
    ) {
      timeout = setTimeout(() => {
        pagedAllFetchState.fetchNextPage()
      }, 200)
    }
    return () => clearTimeout(timeout)
  }, [
    pagedAllFetchState.hasNextPage,
    pagedAllFetchState.fetchNextPage,
    pagedAllFetchState.isFetchingNextPage,
    pagedAllFetchState
  ])
}

export function useGetGameDay(
  guid: string,
  queryOptions?: Partial<QueryObserverOptions<GameDay, AxiosError>>
): UseQueryResult<GameDay> {
  const set = useGameDaysStore((state) => state.set)
  const initialData = useGameDaysStore((state) => state.gamedays[guid])
  const gameDayFetchState = useGremlinQuery<GameDay>(
    `/gamedays/${guid}`,
    {},
    {
      placeholderData: initialData,
      enabled: Boolean(guid),
      ...queryOptions,
      onSuccess: (data) => {
        set((state) => {
          state.gamedays[data.guid] = data
        })
      }
    }
  )

  return gameDayFetchState
}
